import React, { useMemo, useEffect, useCallback, useState, useRef } from 'react';
import { FaArrowLeft, FaArrowRight, FaBolt } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';

import RouteGuard from './components/RouteGuard';
import BatchHeader from './components/BatchHeader';
import Stepper from './components/Stepper';
import ProductModulePanel from './components/ProductModulePanel';
import RecentFilters from './components/RecentFilters';
import MacroFunctionList from './components/MacroFunctionList';
import TablesDedup from './components/TablesDedup';
import SkeletonSelector from './components/SkeletonSelector';
import PlanReview from './components/PlanReview';
import RunProgress from './components/RunProgress';
import ResultsPanel from './components/ResultsPanel';
import SummaryPanel from './components/SummaryPanel';
import HistoryPanel from './components/HistoryPanel';
import { Button } from './components/Primitives';

import { useBatchResources } from './hooks/useBatchResources';
import { useSelectionState } from './hooks/useSelectionState';
import { useRecentMacroFunctions } from './hooks/useRecentMacroFunctions';
import { usePlan } from './hooks/usePlan';
import { useBatchRunner } from './hooks/useBatchRunner';
import { useBatchArtifacts } from './hooks/useBatchArtifacts';
import { useBatchHistory } from './hooks/useBatchHistory';
import { useRecentChangeDetails } from './hooks/useRecentChangeDetails';

import {
  STEPS,
  LOG_LEVELS,
  TIME_PRESETS,
  RECENT_STATUS_FILTERS,
  TO_GENERATE_STATUSES,
  REGENERATION_MODE,
  isTerminalStatus,
  normalizeUsername,
} from './utils/constants';
import { dedupeTables, toIsoUtc, sortRecentItems, recentGenStatus } from './utils/format';

/** Calcola i parametri temporali server-side dal preset selezionato. */
function computeTimeParams(filters) {
  const { timePreset, since, until } = filters;
  if (timePreset === 'custom') {
    return { since: toIsoUtc(since), until: toIsoUtc(until) };
  }
  const preset = TIME_PRESETS.find((p) => p.id === timePreset);
  if (preset?.days) {
    const d = new Date();
    d.setDate(d.getDate() - preset.days);
    return { since: d.toISOString(), until: null };
  }
  // 'all': supera il default backend di 30 giorni con un since lontano nel tempo.
  return { since: '2000-01-01T00:00:00Z', until: null };
}

function GeneratorBatchContent() {
  const { user } = useAuth();
  const username = normalizeUsername(user);

  const resources = useBatchResources();
  const { state, actions, isMacroSelected, isSkeletonSelected } = useSelectionState();
  const plan = usePlan();
  const runner = useBatchRunner();
  const history = useBatchHistory();
  const { loadModulesForProduct } = resources;
  const { reset: resetPlan } = plan;

  // Metadati persistenti delle macrofunzioni conosciute (per il riepilogo anche
  // quando i filtri cambiano la lista corrente).
  const [knownItems, setKnownItems] = useState({});

  // Carica i moduli quando cambia il prodotto.
  useEffect(() => {
    if (state.productId) loadModulesForProduct(state.productId);
  }, [state.productId, loadModulesForProduct]);

  // Parametri di filtro server-side per le modifiche recenti.
  const timeParams = useMemo(() => computeTimeParams(state.filters), [state.filters]);
  const recent = useRecentMacroFunctions({
    productId: state.productId,
    moduleIds: state.moduleIds,
    mine: state.filters.mine,
    since: timeParams.since,
    until: timeParams.until,
  });

  // Aggiorna la mappa dei conosciuti quando arrivano nuovi item.
  useEffect(() => {
    if (recent.items.length === 0) return;
    setKnownItems((prev) => {
      const next = { ...prev };
      for (const it of recent.items) next[it.id] = it;
      return next;
    });
  }, [recent.items]);

  // Dettaglio lazy delle modifiche (caricato solo all'espansione di una scheda).
  const changeDetails = useRecentChangeDetails({
    since: timeParams.since,
    until: timeParams.until,
  });

  // Filtri client-side (stato generale + ricerca testuale) e ordinamento.
  const visibleItems = useMemo(() => {
    const q = state.filters.search.trim().toLowerCase();
    const statusFilter = RECENT_STATUS_FILTERS.find(
      (f) => f.id === (state.filters.genStatus || 'all'),
    );
    const statuses = statusFilter?.statuses || null;

    let list = recent.items;
    if (statuses) {
      list = list.filter((it) => statuses.includes(recentGenStatus(it)));
    }
    if (q) {
      list = list.filter(
        (it) =>
          String(it.name || '').toLowerCase().includes(q) ||
          String(it.description || '').toLowerCase().includes(q) ||
          String(it.tableName || '').toLowerCase().includes(q),
      );
    }
    return sortRecentItems(list);
  }, [recent.items, state.filters.search, state.filters.genStatus]);

  // Macrofunzioni selezionate (oggetti completi) e tabelle deduplicate.
  const selectedItems = useMemo(
    () => state.macroFunctionIds.map((id) => knownItems[id]).filter(Boolean),
    [state.macroFunctionIds, knownItems],
  );
  const dedupedTables = useMemo(() => dedupeTables(selectedItems), [selectedItems]);

  // Body comune a piano e creazione. Include regenerationMode: cambiare modalità
  // deve quindi invalidare il piano (tramite selectionFingerprint).
  const requestBody = useMemo(
    () => ({
      macroFunctionIds: state.macroFunctionIds,
      skeletons: state.skeletons,
      logLevel: state.logLevel,
      regenerationMode: state.regenerationMode,
    }),
    [state.macroFunctionIds, state.skeletons, state.logLevel, state.regenerationMode],
  );

  // Un piano descrive esattamente la selezione con cui e' stato calcolato:
  // qualunque variazione deve invalidarlo prima di consentire l'avvio.
  const selectionFingerprint = useMemo(
    () => JSON.stringify(requestBody),
    [requestBody],
  );
  useEffect(() => {
    resetPlan();
  }, [selectionFingerprint, resetPlan]);

  // Artefatti del batch corrente (file, preview, download).
  const batchId = runner.batch?.batchId || null;
  const artifacts = useBatchArtifacts(batchId);

  // Quando il batch raggiunge uno stato terminale, carica i file una volta.
  const batchStatus = runner.batch?.status;
  useEffect(() => {
    if (batchId && isTerminalStatus(batchStatus)) {
      artifacts.loadFiles();
    }
    // artifacts.loadFiles è stabile per batchId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, batchStatus]);

  // --- Derivazioni per il riepilogo e i gate di navigazione ---
  const macroCount = state.macroFunctionIds.length;
  const skeletonCount = state.skeletons.length;
  const incompatibleCount =
    (plan.plan?.skeletons?.incompatible?.length || 0) ||
    (plan.blockingErrors?.length || 0);

  const canPlan = macroCount > 0 && skeletonCount > 0;
  const planIsValid =
    !!plan.plan &&
    (plan.blockingErrors?.length || 0) === 0 &&
    (plan.plan.expectedExecutions ?? plan.plan.tasks?.length ?? 0) > 0;
  const canStart = planIsValid && !runner.starting;

  // Piano calcolato ma nulla da eseguire: tutto già aggiornato.
  const allUpToDate =
    !!plan.plan &&
    (plan.blockingErrors?.length || 0) === 0 &&
    (plan.plan.expectedExecutions ?? 0) === 0 &&
    (plan.plan.totalCombinations ?? 0) > 0;

  // Reachability degli step.
  const reachable = useMemo(() => {
    const set = new Set(['source', 'recent']);
    if (macroCount > 0) set.add('skeletons');
    if (canPlan) set.add('plan');
    if (runner.batch) {
      set.add('run');
      if (isTerminalStatus(runner.batch.status)) set.add('results');
    }
    return set;
  }, [macroCount, canPlan, runner.batch]);

  // --- Azioni ---
  const handleBuildPlan = useCallback(() => {
    plan.build(requestBody).then((res) => {
      if (res) actions.setStep('plan');
    });
  }, [plan, requestBody, actions]);

  const handleStartBatch = useCallback(() => {
    runner.start(requestBody).then((created) => {
      if (created) {
        actions.setStep('run');
        history.reload();
      }
    });
  }, [runner, requestBody, actions, history]);

  const handleOpenHistory = useCallback(
    (id) => {
      runner.loadExisting(id);
      actions.setStep('run');
    },
    [runner, actions],
  );

  // Forza la rigenerazione completa: passa a ALL e ricalcola il piano. Il
  // ricalcolo avviene nell'effetto sotto, DOPO l'invalidazione del piano, così
  // il nuovo piano non viene azzerato dalla corsa fra i due effetti.
  const rebuildAfterModeRef = useRef(false);
  const handleForceAll = useCallback(() => {
    runner.clearNoOutdated?.();
    rebuildAfterModeRef.current = true;
    actions.setRegenerationMode(REGENERATION_MODE.ALL);
  }, [runner, actions]);

  useEffect(() => {
    if (!rebuildAfterModeRef.current) return;
    rebuildAfterModeRef.current = false;
    if (macroCount > 0 && skeletonCount > 0) {
      plan.build(requestBody).then((res) => {
        if (res) actions.setStep('plan');
      });
    }
    // Reagisce al solo cambio di modalità; requestBody è già coerente qui.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.regenerationMode]);

  // Dopo uno stato terminale del batch: ricarica recenti e storico e rivaluta il
  // piano se selezione e scheletri sono ancora presenti. Guardia per batchId +
  // stato, così non si ripete a ogni render/poll.
  const terminalHandledRef = useRef(null);
  useEffect(() => {
    if (!batchId || !isTerminalStatus(batchStatus)) return;
    const key = `${batchId}:${batchStatus}`;
    if (terminalHandledRef.current === key) return;
    terminalHandledRef.current = key;
    recent.reload();
    history.reload();
    if (state.macroFunctionIds.length > 0 && state.skeletons.length > 0) {
      plan.build(requestBody);
    }
    // Volutamente ristretto a batchId + stato terminale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, batchStatus]);

  // Selezione mirata: solo le macrofunzioni "da generare" tra quelle visibili.
  const handleSelectToGenerate = useCallback(() => {
    const ids = visibleItems
      .filter((it) => TO_GENERATE_STATUSES.has(recentGenStatus(it)))
      .map((it) => it.id);
    actions.setMacros(ids);
  }, [visibleItems, actions]);

  // Navigazione avanti/indietro.
  const currentIndex = STEPS.findIndex((s) => s.id === state.step);
  const goPrev = () => {
    if (currentIndex > 0) actions.setStep(STEPS[currentIndex - 1].id);
  };
  const goNext = () => {
    const next = STEPS[currentIndex + 1];
    if (next && reachable.has(next.id)) actions.setStep(next.id);
  };
  const nextReachable = STEPS[currentIndex + 1] && reachable.has(STEPS[currentIndex + 1].id);

  const systemOnline = !resources.loading && !resources.error;

  return (
    <div className="mx-auto max-w-7xl space-y-5 pb-10">
      <BatchHeader systemOnline={systemOnline} username={username} />

      <div className="rounded-2xl border border-gray-200 bg-white/60 p-3 shadow-sm backdrop-blur dark:border-gray-700 dark:bg-gray-800/60">
        <Stepper current={state.step} reachable={reachable} onSelect={actions.setStep} />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        {/* Colonna principale */}
        <div className="space-y-5 lg:col-span-2">
          {resources.error && (
            <div className="rounded-2xl bg-red-50 p-4 text-sm text-red-700 dark:bg-red-900/30 dark:text-red-300">
              {resources.error}
            </div>
          )}

          {state.step === 'source' && (
            <ProductModulePanel
              products={resources.products}
              modules={resources.modulesByProduct[state.productId] || []}
              modulesLoading={resources.modulesLoading}
              productId={state.productId}
              moduleIds={state.moduleIds}
              onSelectProduct={actions.setProduct}
              onToggleModule={actions.toggleModule}
              onSelectAllModules={() =>
                actions.setModules(
                  (resources.modulesByProduct[state.productId] || []).map((m) => m.id),
                )
              }
              onClearModules={() => actions.setModules([])}
            />
          )}

          {state.step === 'recent' && (
            <>
              <RecentFilters
                filters={state.filters}
                onSetFilter={actions.setFilter}
                onReload={recent.reload}
                loading={recent.loading}
              />
              <MacroFunctionList
                items={visibleItems}
                loading={recent.loading}
                error={recent.error}
                isSelected={isMacroSelected}
                onToggle={actions.toggleMacro}
                onSelectVisible={() => actions.setMacros(visibleItems.map((it) => it.id))}
                onSelectToGenerate={handleSelectToGenerate}
                onClearAll={actions.clearMacros}
                selectedCount={macroCount}
                isExpanded={changeDetails.isExpanded}
                onToggleExpand={changeDetails.toggle}
                getDetail={changeDetails.getDetail}
                onRetryDetail={changeDetails.retry}
              />
              <TablesDedup tables={dedupedTables} />
            </>
          )}

          {state.step === 'skeletons' && (
            <SkeletonSelector
              skeletonRoots={resources.skeletonRoots}
              skeletonsByRoot={resources.skeletonsByRoot}
              onLoadRoot={resources.loadSkeletonsForRoot}
              selectedSkeletons={state.skeletons}
              isSkeletonSelected={isSkeletonSelected}
              onToggleSkeleton={actions.toggleSkeleton}
              onClear={actions.clearSkeletons}
            />
          )}

          {state.step === 'plan' && (
            <PlanReview
              plan={plan.plan}
              blockingErrors={plan.blockingErrors}
              error={plan.error}
              loading={plan.loading}
              regenerationMode={state.regenerationMode}
            />
          )}

          {state.step === 'run' && (
            <RunProgress
              batch={runner.batch}
              polling={runner.polling}
              cancelling={runner.cancelling}
              onCancel={runner.cancel}
            />
          )}

          {state.step === 'results' && (
            <ResultsPanel
              status={runner.batch?.status}
              files={artifacts.files}
              filesLoading={artifacts.filesLoading}
              filesError={artifacts.filesError}
              activeFileId={artifacts.activeFileId}
              preview={artifacts.preview}
              previewLoading={artifacts.previewLoading}
              previewError={artifacts.previewError}
              onOpenPreview={artifacts.openPreview}
              onClosePreview={artifacts.closePreview}
              downloading={artifacts.downloading}
              onDownload={artifacts.download}
            />
          )}

          {/* Navigazione avanti/indietro */}
          <div className="flex items-center justify-between">
            <Button variant="ghost" icon={FaArrowLeft} onClick={goPrev} disabled={currentIndex === 0}>
              Indietro
            </Button>
            {currentIndex < STEPS.length - 1 && (
              <Button variant="secondary" onClick={goNext} disabled={!nextReachable}>
                Avanti <FaArrowRight size={14} />
              </Button>
            )}
          </div>
        </div>

        {/* Colonna laterale: riepilogo sticky + storico */}
        <aside className="space-y-5">
          <div className="lg:sticky lg:top-24">
            <SummaryPanel
              macroCount={macroCount}
              tableCount={dedupedTables.length}
              skeletonCount={skeletonCount}
              plan={plan.plan}
              incompatibleCount={incompatibleCount}
              regenerationMode={state.regenerationMode}
              onSetRegenerationMode={actions.setRegenerationMode}
              onBuildPlan={handleBuildPlan}
              onStartBatch={handleStartBatch}
              onForceAll={handleForceAll}
              planLoading={plan.loading}
              starting={runner.starting}
              canPlan={canPlan}
              canStart={canStart}
              allUpToDate={allUpToDate}
              logLevel={state.logLevel}
              onSetLogLevel={actions.setLogLevel}
              logLevels={LOG_LEVELS}
            />
            {runner.noOutdatedTasks ? (
              <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-700 dark:border-emerald-800/60 dark:bg-emerald-900/20 dark:text-emerald-300">
                <p>{runner.error}</p>
                <button
                  type="button"
                  onClick={handleForceAll}
                  className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-2.5 py-1 font-medium text-white hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
                >
                  <FaBolt size={11} /> Forza rigenerazione completa
                </button>
              </div>
            ) : (
              (runner.error || plan.error) && (
                <div className="mt-3 rounded-xl bg-red-50 p-3 text-xs text-red-700 dark:bg-red-900/30 dark:text-red-300">
                  {runner.error || plan.error}
                </div>
              )
            )}
          </div>

          <HistoryPanel
            items={history.items}
            loading={history.loading}
            error={history.error}
            onReload={history.reload}
            onOpen={handleOpenHistory}
            activeBatchId={batchId}
          />
        </aside>
      </div>
    </div>
  );
}

/**
 * Pagina Generatore Batch (Paul-only). Il guard è applicato qui, oltre che a
 * livello di route/menu: un utente diverso che apre l'URL viene reindirizzato.
 */
export default function GeneratorBatchPage() {
  return (
    <RouteGuard>
      <GeneratorBatchContent />
    </RouteGuard>
  );
}
